/*
 * Copyright (c) 2014-2023 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, OnInit, Input as ParamsInput, ViewChild, ElementRef, OnDestroy} from '@angular/core';
import {Page} from '../../../model/page';
import {Transaction} from '../../../model/transaction';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/delay';
import {ToastrService} from 'ngx-toastr';
import {Router} from '@angular/router';
import * as _ from 'lodash';
import {permissions} from '../../../permissions';
import {TransactionService} from '../../../services/transaction.service';
import {GridFiltersService} from '../../../services/grid.filters.service';
import {DatesService} from '../../../services/dates.service';
import {UserProfileService} from '../../../services/user.profile.service';
import {AuthService} from '../../../auth/auth.service';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';
import {MatDialog} from '@angular/material';
import {TransactionsEmailModalComponent} from '../transactions-email-modal/transactions-email-modal.component'

@Component({
    selector: 'app-transactions',
    templateUrl: 'transactions-inventory-download.component.html',
    styleUrls: ['transactions-inventory-download.component.scss'],
    providers: [
        TransactionService,
        GridFiltersService,
        DatesService,
        UserProfileService,
        AuthService
    ]
})
export class TransactionsInventoryDownloadComponent implements OnInit, OnDestroy {

    @ViewChild('myTable')       table: any;
    @ViewChild('resendBtn')     resendBtn: any;
    @ParamsInput()              buttons: any;

    @ViewChild('transactionsList') transactionsList: ElementRef;
    @ViewChild('actionBar') actionBar: ElementRef;
    @ViewChild('tableHeader') tableHeader: ElementRef;
    @ViewChild('tableFilter') tableFilter: ElementRef;
    @ViewChild('paginator') paginator: ElementRef;

    public loading = false;
    page = new Page();
    cols: any[];
    rows = new Array<Transaction>();
    public selected: any = null;
    public totalRecords = 0;
    public resendEnable = false;

    public filters: { [key: string]: any; } = {};
    public showFilters: boolean;
    public filtersApplied: boolean;

    public customers: Array<any> = [];
    public companies: Array<any> = [];
    public vendors:   Array<any> = [];
    public ediTypes: Array<any>  = [];

    public statuses: Array<any> = [
        {value: TransactionService.STATUS_NOT_SENT,   text: 'Not Sent'},
        {value: TransactionService.STATUS_PROCESSING, text: 'Processing'},
        {value: TransactionService.STATUS_SENT,       text: 'Sent'},
        {value: TransactionService.STATUS_FAILED,     text: 'Failed'},
        {value: TransactionService.STATUS_RESENDING,  text: 'Resending'}
    ];

    public inOutBound: Array<any> = [
        {value: null, text: 'All'},
        {value: '0',  text: 'Inbound'},
        {value: '1',  text: 'Outbound'},
    ];

    public modes: Array<any> = [
        {value: null, text: 'All'},
        {value: '0',  text: 'Production'},
        {value: '1',  text: 'Test'},
    ];
    private preloadedData: {};

    public scrollHeight = '50px';
    private viewportHeight = 0;
    private viewportHeightListener: boolean;

    private subscriptions: Array<Subscription>;

    public permissions = permissions;

    // Email table  variables
    public emailCols: any[];
    public emailSelected: any = null;

    // Full Text Search
    public SEARCH: any;
    public searchString = '';
    public searchInfo = false;
    public searchApplied = false;

    constructor(
        private transactionService: TransactionService,
        private toastr: ToastrService,
        private datesService: DatesService,
        private router: Router,
        private gridFiltersService: GridFiltersService,
        private userProfileService: UserProfileService,
        private authService: AuthService,
        private title: Title,
        private dialog: MatDialog
    ) {
        this.page.pageNumber = 0;
        this.page.size = 50;
        this.buttons = {search: true, export: true, resend: true};
        this.title.setTitle('EDI - Transactions Inventory Download');
    }

    ngOnInit() {
        this.loading = true;
        this.showFilters = true;
        this.filtersApplied = false;
        this.viewportHeightListener = true;
        this.subscriptions = [];

        const tableSettings = this.authService.tableSettings || {};
        this.cols = [
            { field: 'CO1_ID',          header: 'Company',          visible: true, width: 75 },
            { field: 'CU1_ID',          header: 'Customers',        visible: true, width: 50 },
            { field: 'VD1_ID',          header: 'Vendors',          visible: true, width: 60 },
            { field: 'ED1_TYPE',        header: 'Type',             visible: true, width: 40 },
            { field: 'ED1_DOCUMENT_NO', header: 'Document No',      visible: true, width: 60 },
            { field: 'ED1_STATUS',      header: 'Status',           visible: true, width: 30 },
            { field: 'ED1_IN_OUT',      header: 'In/Out',           visible: true, width: 40 },
            { field: 'ED1_TEST_MODE',   header: 'Mode',             visible: true, width: 60 },
            { field: 'ED1_MODIFIED_ON', header: 'Modified On',      visible: true, width: 70 },
            { field: 'ED1_INVENTORY_PATH', header: 'Download Inventory', visible: true, width: 70 }
        ];

        this.emailCols = [
            { field: 'EM1_ID', header: 'Email ID', visible: true, tooltip: 'Email ID', width: 100 },
            { field: 'EM1_RECIPIENT', header: 'Recipient', visible: true, tooltip: 'Recipient', width: 200 },
            { field: 'EM1_COPY_TO', header: 'Copy To', visible: true, tooltip: 'Copy To', width: 140 },
            { field: 'EM1_SUBJECT', header: 'Subject', visible: true, tooltip: 'Subject', width: 280 },
            { field: 'EM1_BODY', header: 'Body', visible: true, tooltip: 'Body', width: 480},
            { field: 'EM1_ATTACHMENTS', header: 'Attachments', visible: true, tooltip: 'Attachments', width: 150 },
            { field: 'EM1_SENT', header: 'Sent', visible: true, tooltip: 'Sent', width: 50 },
            { field: 'EM1_CREATED_ON', header: 'Created On', visible: true, tooltip: 'Created On', width: 165 }
        ];

        this.subscriptions.push(
            this.transactionService.preloadData().subscribe( data => {
                this.preloadedData = data;
                this.prepareData(data);
                this.setPage({offset: 0});
            })
        );
        this.screenHeightListener();
    }

    ngOnDestroy() {
        this.viewportHeightListener = false;
        for (let index = 0; index < this.subscriptions.length; index++) {
            if (this.subscriptions[index]) {
                this.subscriptions[index].unsubscribe();
            }
        }
    }

    prepareData(data) {
        this.companies = data.companies.map( company => {
            return {
                CO1_ID: Number(company.CO1_ID),
                text:   company.CO1_NAME
            }
        });

        this.ediTypes = data.ediTypes.map( ediType => {
            return {
                ET1_NAME: ediType.ET1_NAME,
                text:     ediType.ET1_NAME
            }
        });

        this.customers = data.customers.map( customer => {
            const text = (customer.CO1_NAME ? customer.CO1_NAME + ': ' : '') +
            (customer.CU1_NAME ? customer.CU1_NAME : '');

            return {
                value:  customer.CO1_ID + '_' + customer.CU1_ID,
                CU1_ID: Number(customer.CU1_ID),
                CO1_ID: Number(customer.CO1_ID),
                text:   text,
                displayFld: customer.CU1_NAME,
                CU1_SHOW_DEFAULT: customer.CU1_SHOW_DEFAULT.toString().toUpperCase()
            }
        }).filter( customer => {
            if (customer.CU1_SHOW_DEFAULT != 'X') {
                return false;
            }
            if (
                this.filters.CO1_ID
                && this.filters.CO1_ID.length
            ) {
                return this.filters.CO1_ID.indexOf(customer.CO1_ID) > -1;
            } else {
                return true;
            }
        });

        this.vendors = data.vendors.map( vendor => {
            const text  = (vendor.CO1_NAME ? vendor.CO1_NAME + ': ' : '') +
                        (vendor.VD1_NAME ? vendor.VD1_NAME : '');
            return {
                value:  vendor.CO1_ID + '_' + vendor.VD1_ID,
                VD1_ID: Number(vendor.VD1_ID),
                CO1_ID: Number(vendor.CO1_ID),
                text:   text,
                displayFld: vendor.VD1_NAME,
                VD1_SHOW_DEFAULT: vendor.VD1_SHOW_DEFAULT.toString().toUpperCase()
            }
        }).filter( vendor => {
            if (vendor.VD1_SHOW_DEFAULT != 'X') {
                return false;
            }
            if (
                this.filters.CO1_ID
                && this.filters.CO1_ID.length
            ) {
                return this.filters.CO1_ID.indexOf(vendor.CO1_ID) > -1;
            } else {
                return true;
            }
        });
    }

    getData() {
        this.setPage({offset: this.page.pageNumber});
    }

    setPage(pageInfo) {
        this.loading = true;
        this.page.pageNumber = pageInfo.offset;

        this.subscriptions.push(
            this.transactionService.getTransactions(this.page, false, true).subscribe(result => {
                this.rows = result.data.map( row => {
                    row['disabled'] = row['ED1_STATUS'] == TransactionService.STATUS_RESENDING;
                    row['ED1_TYPE'] = row['ED1_TYPE'].replace(/\0/g, '');
                    return row;
                });
                this.totalRecords = result.count;
                this.loading = false;
            }, () => {
                this.loading = false;
                window.dispatchEvent(new Event('resize'));
                this.toastr.error('Transaction loading error occurred', 'Service Error', {
                    timeOut: 3000,
                });
            })
        );
    }

    getContentDialogHeight(): number {
        // 77 for header & footer
        return (window.innerHeight) - ((this.actionBar ? this.actionBar.nativeElement.offsetHeight : 0) + 48) - 77;
    }

    getScrollHeight(): number {
        return this.getContentDialogHeight()
            - (
                this.tableHeader.nativeElement.offsetHeight
                + (this.paginator.nativeElement.offsetHeight + 55)
                + ((this.showFilters && this.tableFilter) ? (this.tableFilter.nativeElement.offsetHeight) : 0)
            );
    }

    onPage(event) {
        this.page.size = event.rows;
        this.setPage({
            offset: event.first / event.rows
        });
    }

    onSort(event) {
        this.page.sortParams = {};
        if (!_.isEmpty(event.sorts)) {
            for (const item of event.sorts) {
                this.page.sortParams[item.prop] = item.dir;
            }
        }
        this.ngOnInit();
    }

    clearFilters() {
        this.filters = {};
        this.showFilters = false;

        this.onSubmit();
    }

    onSubmit() {
        this.prepareData(this.preloadedData);

        const filters = _.mapValues(this.gridFiltersService.prepare( this.filters), (value, key) => {
            if ( /CU1_ID/.test(key) || /VD1_ID/.test(key)) {
                const val = value.split('_');
                if (/CU1_ID/.test(key)) {
                    value = JSON.stringify({CO1_ID: val[0], CU1_ID: val[1]});
                } else {
                    value = JSON.stringify({CO1_ID: val[0], VD1_ID: val[1]});
                }

                if (
                    this.filters.CO1_ID
                    && this.filters.CO1_ID.indexOf( Number(val[0]) ) == -1
                ) {
                    value = null;
                }
            }

            return value;
        });

        if (filters['ED1_MODIFIED_ON']) {
            filters['ED1_MODIFIED_ON_FROM'] = this.datesService.format(filters['ED1_MODIFIED_ON']);
            filters['ED1_MODIFIED_ON_TO']   = this.datesService.format(filters['ED1_MODIFIED_ON'], '23:59:59');
            delete filters['ED1_MODIFIED_ON'];
        }

        this.filtersApplied = Object.keys(filters).length > 0;

        let reload = false;
        const keys = Object.keys(filters).concat(Object.keys(this.page.filterParams));
        for (const key of keys) {
            if (this.page.filterParams[key] != filters[key]) {
                reload = true;
                break;
            }
        }

        if (reload) {
            this.page.filterParams = filters;
            this.getData();
        }
    }

    onColResize(event) {
        const cellIndex = event.element.cellIndex - 2;
        if (cellIndex > -1) {
            const settings = {};
            if (this.tableHeader.nativeElement.children.length > 0) {
              for (let i = 0; i < this.tableHeader.nativeElement.children.length; i++) {
                if (i >= 2) {
                    const td = this.tableHeader.nativeElement.children[i];
                    this.cols[i - 2]['width'] = td.offsetWidth;
                    settings[this.cols[i - 2]['field']] = td.offsetWidth;
                }
              }
            }

            const tableSettings = JSON.stringify(settings);
            this.authService.setTableSettings(tableSettings);
            const data = {
                user_id: this.authService.userId,
                UP1_TRANSACTION_TABLE_SETTINGS: tableSettings
            };
            this.userProfileService.updateTableSettings(data).subscribe( result => {});
        }
    }

    onRowExpand($event) {
        const row = $event.data;

        if (!row.logs) {
            row.logs = {
                items: [],
                loaded: false
            }
        }

        row.emails = {
            items: [],
            loaded: false
        }

        this.loadEmails(row);

        this.transactionService.getDocument(row.ED1_ID, row.CO1_ID).subscribe( result => {
            row.document = result.data[0];
        });

        if (!row.logs.loaded) {
            this.loading = true;
            return this.transactionService.getLogs(row.CO1_ID, row.ED1_ID).subscribe( result => {
                this.loading = false;
                this.rows = this.rows.map( item => {
                    if (
                        item['CO1_ID'] == row.CO1_ID
                        && item['ED1_ID'] == row.ED1_ID
                    ) {
                        item['logs'] = {
                            items: result.data,
                            loaded: true
                        };
                    }
                    return item;
                });
                this.loading = false;
            });
        }
    }

    resendTransactions() {
        if (this.selected.length) {
            this.loading = true;
            this.subscriptions.push(
                this.transactionService.resend(this.selected).subscribe( results => {
                    this.selected = this.selected.filter( row => {
                        let item = null;
                        results.forEach( result => {
                            result.transactions.forEach( transaction => {
                                if (
                                    row['CO1_ID'] == transaction['CO1_ID']
                                    && row['ED1_ID'] == transaction['ED1_ID']
                                ) {
                                    row['ED1_STATUS'] = transaction['ED1_STATUS'];
                                    if (row['ED1_STATUS'] != TransactionService.STATUS_RESENDING) {
                                        item = row;
                                    }
                                }
                            });
                        });
                        return item || false;
                    });
                    this.loading = false;
                    this.resendEnable = this.hasSelection();
                })
            );
        }
    }

    onRowSelect() {
        this.resendEnable = this.hasSelection();
    }

    onRowUnselect() {
        this.resendEnable = this.hasSelection();
    }

    onHeaderCheckboxToggle() {
        this.resendEnable = this.hasSelection();
    }

    private hasSelection() {
        let hasSelection = false;
        this.selected.forEach( row => {
            if (row['ED1_STATUS'] != TransactionService.STATUS_RESENDING) {
                hasSelection = true;
            }
        });
        return hasSelection;
    }

    onWindowResize() {
        setTimeout(() => {
            this.scrollHeight = this.getScrollHeight() + 'px';
            window.dispatchEvent(new Event('resize'));
        }, 150);
    }

    screenHeightListener() {
        if (!this.viewportHeightListener) {
            return;
        }
        if (window.innerHeight != this.viewportHeight) {
            this.viewportHeight = window.innerHeight;
            this.onWindowResize();
        }
        setTimeout(() => {
            this.screenHeightListener();
        }, 500);
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
        this.onWindowResize();
    }

    private loadEmails(row: any) {
        this.loading = true;
        return this.transactionService.getEmails(row.CO1_ID, row.ED1_ID).subscribe( result => {
            this.loading = false;
            this.rows = this.rows.map( item => {
                if (
                    item['CO1_ID'] == row.CO1_ID
                    && item['ED1_ID'] == row.ED1_ID
                ) {
                    item['emails'] = {
                        items: result.data,
                        loaded: true
                    };
                }
                return item;
            });
            this.loading = false;
        });
    }

    loadEmailModal(emailRowData) {
        const dialogRef = this.dialog.open(TransactionsEmailModalComponent, {
            width: window.innerWidth - 100 + 'px',
            height: window.innerHeight - 80 + 'px',
            role: 'dialog',
            data: { subject: emailRowData['EM1_SUBJECT'], body: emailRowData['EM1_BODY'] },
        });
    }

    onSubmitSearch() {
        if ( this.searchString.length > 2 ) {
            this.loading = true;
            this.searchApplied = true;
            const search = this.searchString.toString().toUpperCase();
            this.page.filterParams = { ['search']: search };
            this.page.pageNumber = 0;

            this.subscriptions.push(
                this.transactionService.getTransactions(this.page).subscribe(result => {
                    this.rows = result.data.map( row => {
                        row['disabled'] = row['ED1_STATUS'] === TransactionService.STATUS_RESENDING;
                        row['ED1_TYPE'] = row['ED1_TYPE'].replace(/\0/g, '');
                        return row;
                    });
                    this.totalRecords = result.count;
                    this.loading = false;
                }, () => {
                    this.loading = false;
                    window.dispatchEvent(new Event('resize'));
                    this.toastr.error('Transaction loading error occurred', 'Service Error', {
                        timeOut: 3000,
                    });
                })
            );
        } else {
            this.loading = false;
            this.toastr.error('Please enter at least three characters into the search input.', 'Search Error', {
                timeOut: 3000,
            });
        }
    }

    clearSearch() {
        this.loading = true;
        this.searchApplied = false;
        this.searchString = '';
        this.page.filterParams = {};
        this.page.pageNumber = 0;

        this.subscriptions.push(
            this.transactionService.getTransactions(this.page).subscribe(result => {
                this.rows = result.data.map( row => {
                    row['disabled'] = row['ED1_STATUS'] === TransactionService.STATUS_RESENDING;
                    row['ED1_TYPE'] = row['ED1_TYPE'].replace(/\0/g, '');
                    return row;
                });
                this.totalRecords = result.count;
                this.loading = false;
            }, () => {
                this.loading = false;
                window.dispatchEvent(new Event('resize'));
                this.toastr.error('Transaction loading error occurred', 'Service Error', {
                    timeOut: 3000,
                });
            })
        );
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/transactions/transactions-inventory-download/transactions-inventory-download.component.ts